import { Button, Flex, FormControl, FormLabel, Text, Textarea } from '@chakra-ui/react'
import { PopUpMessageContext } from '@hb/react'
import { useCallback, useContext, useState } from 'react'
import { DefaultModal } from '../components/Modals/DefaultModal'

// §1.6.2 shared hold/release reason modal — one free-text field plus confirm. Used by
// the visits BillingHoldModal and the encounter footer hold/release flows. onSubmit
// performs the callable (and any success toast); a rejection keeps the modal open and
// surfaces the error via PopUpMessageContext.
export interface BillingHoldReasonModalProps {
  isOpen: boolean
  onClose: () => void
  header: string
  // Optional bold lead-in line inside the body (the visits modal renders one).
  title?: string
  description?: string
  reasonLabel: string
  reasonPlaceholder?: string
  reasonRequired: boolean
  confirmLabel: string
  errorMessage: string
  onSubmit: (reason: string) => Promise<void>
}

const BillingHoldReasonModalBody = ({
  onClose,
  title,
  description,
  reasonLabel,
  reasonPlaceholder,
  reasonRequired,
  confirmLabel,
  errorMessage,
  onSubmit,
}: Omit<BillingHoldReasonModalProps, 'isOpen' | 'header'>) => {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const { processResponse } = useContext(PopUpMessageContext)

  const handleConfirm = useCallback(async () => {
    const trimmed = reason.trim()
    if (reasonRequired && !trimmed) return
    setLoading(true)
    try {
      await onSubmit(trimmed)
      onClose()
    } catch (err: any) {
      processResponse({ error: err?.message ?? errorMessage })
    }
    setLoading(false)
  }, [reason, reasonRequired, onSubmit, onClose, processResponse, errorMessage])

  return (
    <Flex flexFlow="column" gap={4} p={4}>
      {title ? (
        <Text fontSize="md" color="gray.700" fontWeight={500}>
          {title}
        </Text>
      ) : null}
      {description ? (
        <Text fontSize="sm" color="gray.500">
          {description}
        </Text>
      ) : null}
      <FormControl>
        <FormLabel fontSize="sm" color="gray.600">
          {reasonLabel}
        </FormLabel>
        <Textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={reasonPlaceholder}
          rows={3}
          autoFocus
        />
      </FormControl>
      <Flex gap={2} justify="flex-end">
        <Button variant="ghost" onClick={onClose} isDisabled={loading}>
          Cancel
        </Button>
        <Button
          colorScheme="orange"
          onClick={handleConfirm}
          isLoading={loading}
          isDisabled={reasonRequired && reason.trim() === ''}>
          {confirmLabel}
        </Button>
      </Flex>
    </Flex>
  )
}

export const BillingHoldReasonModal = ({
  isOpen,
  header,
  ...props
}: BillingHoldReasonModalProps) => (
  <DefaultModal
    isOpen={isOpen}
    onClose={props.onClose}
    header={header}
    size="md"
    render={() => <BillingHoldReasonModalBody {...props} />}
  />
)
